/* eslint-disable react-hooks/exhaustive-deps */
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
} from "react";
import SignaturePad from "signature_pad";

interface SignaturePadProps {
  penColor?: string;
  canvasProps?: React.CanvasHTMLAttributes<HTMLCanvasElement>;
  onEnd?: () => void;
  onBegin?: () => void;
  clearOnResize?: boolean;
  // width?: number;
  height?: number;
}

export interface SignaturePadRef {
  clear: () => void;
  isEmpty: () => boolean;
  toDataURL: (type?: string, encoderOptions?: number) => string;
  fromDataURL: (dataUrl: string) => void;
}

const SignaturePadComponent = forwardRef<SignaturePadRef, SignaturePadProps>(
  (
    {
      penColor = "black",
      canvasProps = {},
      onEnd,
      onBegin,
      clearOnResize = false,
      height = 128, // Keep height fixed
    },
    ref
  ) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const signaturePadRef = useRef<SignaturePad | null>(null);
    const isDrawingRef = useRef(false);

    // In SignaturePadComponent.tsx
    const handleEnd = useCallback(() => {
      onEnd?.();
    }, [onEnd]);

    const handleBegin = useCallback(() => {
      onBegin?.();
    }, [onBegin]);

    // Initialize SignaturePad
    useEffect(() => {
      if (canvasRef.current) {
        const canvas = canvasRef.current;
        const parentWidth = canvas.parentElement?.offsetWidth || 370; // Dynamic width

        // Set canvas size
        canvas.width = parentWidth;
        canvas.height = height;

        const signaturePad = new SignaturePad(canvas, {
          backgroundColor: "rgb(240, 240, 240)",
          penColor,
        });

        signaturePadRef.current = signaturePad;

        // Store original event handlers
        const originalMouseDown = (signaturePad as any)._handleMouseDown?.bind(
          signaturePad
        );
        const originalMouseUp = (signaturePad as any)._handleMouseUp?.bind(
          signaturePad
        );

        // Add begin/end event handling
        (signaturePad as any)._handleMouseDown = (
          event: MouseEvent | TouchEvent
        ) => {
          isDrawingRef.current = true;
          handleBegin?.();
          originalMouseDown?.(event);
        };

        (signaturePad as any)._handleMouseUp = (
          event: MouseEvent | TouchEvent
        ) => {
          isDrawingRef.current = false;
          handleEnd?.();
          originalMouseUp?.(event);
        };

        // Handle window resize
        const handleResize = () => {
          if (!canvasRef.current) return;
          const newWidth = canvas.parentElement?.offsetWidth || 370;
          const data = signaturePad.toData();
          canvas.width = newWidth;
          canvas.height = height;
          signaturePad.fromData(data);
        };

        window.addEventListener("resize", handleResize);

        return () => {
          signaturePad.off();
          window.removeEventListener("resize", handleResize);
        };
      }
    }, [height, penColor]);

    // Update pen color when changed
    useEffect(() => {
      if (signaturePadRef.current) {
        signaturePadRef.current.penColor = penColor;
      }
    }, [penColor]);

    // Expose methods via ref
    useImperativeHandle(ref, () => ({
      clear: () => {
        signaturePadRef.current?.clear();
      },
      isEmpty: () => {
        return signaturePadRef.current?.isEmpty() ?? true;
      },
      toDataURL: (type?: string, encoderOptions?: number) => {
        return signaturePadRef.current?.toDataURL(type, encoderOptions) ?? "";
      },
      fromDataURL: (dataUrl: string) => {
        signaturePadRef.current?.fromDataURL(dataUrl);
      },
    }));

    // Handle touch events to prevent scrolling
    useEffect(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const handleTouchMove = (e: TouchEvent) => {
        if (isDrawingRef.current) {
          e.preventDefault();
        }
      };

      canvas.addEventListener("touchmove", handleTouchMove, { passive: false });

      return () => {
        canvas.removeEventListener("touchmove", handleTouchMove);
      };
    }, []);

    return (
      <div style={{ width: "100%", position: "relative", touchAction: "none" }}>
        <canvas
          ref={canvasRef}
          {...canvasProps}
          style={{
            width: "100%", // This ensures responsiveness
            height: `${height}px`,
            border: "1px solid #ccc",
            borderRadius: "4px",
            backgroundColor: "rgb(240, 240, 240)",
          }}
        />
      </div>
    );
  }
);

SignaturePadComponent.displayName = "SignaturePadComponent";

export default SignaturePadComponent;
